import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { getWorkShifts, deleteWorkShift, clearError, clearSuccess } from '../../../../redux/Slices/authSlice';
import { FaSearch, FaPlus, FaEdit, FaTrash, FaEye, FaPrint, FaFileExcel } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import ReactDOM from 'react-dom';
import { useTitle } from '../../../../shared/hooks/TitleContext';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';
import PdfPreviewModal from '../../Accounting/Reports/components/PdfPreviewModal';
import { chunkArray, toPositiveInteger } from '../../../../shared/utils.js/utils';
import SortOrderSelect from '../../../../shared/components/SortOrderSelect';

const Shifts = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setTitle } = useTitle();
  const { t, i18n } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const { workShifts, isLoading, error, success } = useSelector((state) => state.auth);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [shiftToDelete, setShiftToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [perPageInput, setPerPageInput] = useState(10);
  const [sortOrder, setSortOrder] = useState('asc');
  const [sortField, setSortField] = useState('name');
  const textAlign = isRTL ? 'right' : 'left';
  const dir = isRTL ? 'rtl' : 'ltr';
  const lastErrorRef = useRef({ message: null, time: 0 });
  const [shiftForPrint, setShiftForPrint] = useState(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfType, setPdfType] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [rawShifts, setRawShifts] = useState([]);
  const [pdfChunks, setPdfChunks] = useState([]);
  const [allShiftsForPdf, setAllShiftsForPdf] = useState([]);
  const [localCurrentPage, setLocalCurrentPage] = useState(1);
  const publicUrl = process.env.PUBLIC_URL || '';
  const headerLogo = `${publicUrl}/ACE.png`;

  useEffect(() => {
    setTitle('sidenav.hr.shifts');
  }, [setTitle]);

  const handleApplyPerPage = () => {
    const total = filteredData?.length || 0;
    const maxValue = total > 0 ? total : 1000;
    const safeValue = Math.max(1, Math.min(maxValue, toPositiveInteger(perPageInput, perPage)));
    setPerPageInput(safeValue);
    setPerPage(safeValue);
    setLocalCurrentPage(1);
  };

  const fetchAllShifts = useCallback(async () => {
    try {
      const params = { language: i18n.language, page: 1, per_page: 1000 };
      const result = await dispatch(getWorkShifts(params)).unwrap();
      const data = result?.data || [];
      setRawShifts(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err !== 'OFFLINE_REQUEST_SAVED') {
        console.error('Failed to fetch shifts:', err);
      }
    }
  }, [dispatch, i18n.language]);

  useEffect(() => {
    fetchAllShifts();
  }, [fetchAllShifts]);

  useEffect(() => {
    setLocalCurrentPage(1);
  }, [searchTerm, statusFilter]);

  useEffect(() => {
    dispatch(clearSuccess());
    dispatch(clearError());
  }, [dispatch]);

  useEffect(() => {
    if (success) {
      toast.success(success, { rtl: isRTL });
      dispatch(clearSuccess());
    }
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
        toast.error(error, { rtl: isRTL });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearError()), 3000);
    }
  }, [success, error, dispatch, isRTL]);

  const getShiftName = (shift) => {
    if (!shift) return '—';
    return isRTL ? (shift.name_ar || shift.name_en || shift.name || '—') : (shift.name_en || shift.name_ar || shift.name || '—');
  };

  const getWorkDaysList = (shift) => {
    if (!shift) return '—';
    if (shift.details && Array.isArray(shift.details)) {
      const workDays = shift.details
        .filter(d => !d.is_off_day)
        .map(d => dayNames[d.day_of_week] || d.day_of_week)
        .join(', ');
      return workDays || '—';
    }
    if (shift.work_days) {
      return Object.keys(shift.work_days)
        .filter(day => shift.work_days[day])
        .map(day => dayNames[day] || day)
        .join(', ') || '—';
    }
    return '—';
  };

  const getStartTime = (shift) => {
    if (!shift) return '—';
    if (shift.details && Array.isArray(shift.details)) {
      const firstWorkDay = shift.details.find(d => !d.is_off_day && d.start_time);
      return firstWorkDay?.start_time || shift.start_time || '—';
    }
    return shift.start_time || '—';
  };

  const getEndTime = (shift) => {
    if (!shift) return '—';
    if (shift.details && Array.isArray(shift.details)) {
      const firstWorkDay = shift.details.find(d => !d.is_off_day && d.end_time);
      return firstWorkDay?.end_time || shift.end_time || '—';
    }
    return shift.end_time || '—';
  };

  const dayNames = {
    'saturday': t('shifts.add.dayLabels.saturday'),
    'sunday': t('shifts.add.dayLabels.sunday'),
    'monday': t('shifts.add.dayLabels.monday'),
    'tuesday': t('shifts.add.dayLabels.tuesday'),
    'wednesday': t('shifts.add.dayLabels.wednesday'),
    'thursday': t('shifts.add.dayLabels.thursday'),
    'friday': t('shifts.add.dayLabels.friday')
  };

  const filteredData = React.useMemo(() => {
    let data = rawShifts;
    
    // Status Filter
    if (statusFilter === 'active') {
      data = data.filter(shift => shift.is_active);
    } else if (statusFilter === 'inactive') {
      data = data.filter(shift => !shift.is_active);
    }

    // Search Query (collapse extra spaces)
    if (searchTerm.trim()) {
      const q = searchTerm.trim().replace(/\s+/g, ' ').toLowerCase();
      data = data.filter((shift) => (
        (shift.name_ar || '').toLowerCase().includes(q) ||
        (shift.name_en || '').toLowerCase().includes(q) ||
        (shift.name || '').toLowerCase().includes(q) ||
        (shift.status || '').toLowerCase().includes(q) ||
        (shift.start_time || '').toLowerCase().includes(q) ||
        (shift.end_time || '').toLowerCase().includes(q)
      ));
    }

    data = [...data].sort((a, b) => {
      if (sortField === 'is_active') {
        const aVal = a.is_active ? 1 : 0;
        const bVal = b.is_active ? 1 : 0;
        return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
      } else {
        const aName = isRTL ? (a.name_ar || a.name_en || a.name || '') : (a.name_en || a.name_ar || a.name || '');
        const bName = isRTL ? (b.name_ar || b.name_en || b.name || '') : (b.name_en || b.name_ar || b.name || '');
        if (sortOrder === 'asc') return aName.localeCompare(bName, isRTL ? 'ar' : 'en');
        return bName.localeCompare(aName, isRTL ? 'ar' : 'en');
      }
    });

    return data;
  }, [rawShifts, searchTerm, statusFilter, sortOrder, sortField, isRTL]);

  const paginatedShifts = React.useMemo(() => {
    const startIndex = (localCurrentPage - 1) * perPage;
    return filteredData.slice(startIndex, startIndex + perPage);
  }, [filteredData, localCurrentPage, perPage]);

  const totalPages = Math.ceil(filteredData.length / perPage);

  const getStatusColor = (status) => {
    return status === t('shifts.status.active') || status === 'نشط' ? '#AC2000' : '#dc3545';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '--';
    return new Date(dateString).toLocaleDateString('en-US');
  };

  const colStyles = {
    serial: { width: '5%', textAlign: 'center', verticalAlign: 'middle', border: '1px solid #000', padding: '6px 2px', fontSize: '10px' },
    name: { width: '27%', textAlign: isRTL ? 'right' : 'left', verticalAlign: 'middle', border: '1px solid #000', padding: '6px 2px', fontSize: '10px', wordBreak: 'break-word' },
    workDays: { width: '23%', textAlign: isRTL ? 'right' : 'left', verticalAlign: 'middle', border: '1px solid #000', padding: '6px 2px', fontSize: '10px', wordBreak: 'break-word' },
    startTime: { width: '12%', textAlign: 'center', verticalAlign: 'middle', border: '1px solid #000', padding: '6px 2px', fontSize: '10px' },
    endTime: { width: '12%', textAlign: 'center', verticalAlign: 'middle', border: '1px solid #000', padding: '6px 2px', fontSize: '10px' },
    status: { width: '10%', textAlign: 'center', verticalAlign: 'middle', border: '1px solid #000', padding: '6px 2px', fontSize: '10px' },
    created: { width: '11%', textAlign: 'center', verticalAlign: 'middle', border: '1px solid #000', padding: '6px 2px', fontSize: '10px' }
  };

  const renderPdfPage = (chunk, chunkIdx, totalPages, elementId = null) => {
    const hasActiveFilters = statusFilter !== 'all' || searchTerm.trim();
    
    const getPreviewShiftName = (shift) => isRTL ? (shift.name_ar || shift.name_en || shift.name || '—') : (shift.name_en || shift.name_ar || shift.name || '—');
    const getPreviewWorkDaysList = (shift) => {
      if (shift.details && Array.isArray(shift.details)) {
        const workDays = shift.details.filter(d => !d.is_off_day).map(d => dayNames[d.day_of_week] || d.day_of_week).join(', ');
        return workDays || '—';
      }
      return shift.work_days ? Object.keys(shift.work_days).filter(day => shift.work_days[day]).map(day => dayNames[day] || day).join(', ') || '—' : '—';
    };
    const getPreviewStartTime = (shift) => {
      if (shift.details && Array.isArray(shift.details)) { const d = shift.details.find(x => !x.is_off_day && x.start_time); return d?.start_time || shift.start_time || '—'; }
      return shift.start_time || '—';
    };
    const getPreviewEndTime = (shift) => {
      if (shift.details && Array.isArray(shift.details)) { const d = shift.details.find(x => !x.is_off_day && x.end_time); return d?.end_time || shift.end_time || '—'; }
      return shift.end_time || '—';
    };
    const previewFormatDate = (ds) => ds ? new Date(ds).toLocaleDateString('en-US') : '--';

    return (
      <div
        id={elementId}
        style={{
          width: '794px',
          height: '1123px',
          backgroundColor: '#ffffff',
          color: '#000000',
          fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
          fontWeight: 'bold',
          padding: '40px',
          boxSizing: 'border-box',
          overflow: 'hidden',
          position: 'relative'
        }}
      >
        <div style={{ border: '5px solid #000', padding: '3px', backgroundColor: '#ffffff', boxSizing: 'border-box', width: '100%', height: '1043px', overflow: 'hidden' }}>
          <div style={{
            border: '2px solid #000',
            height: '1033px',
            padding: '18px 18px 10px',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif',
            fontWeight: 'bold',
            WebkitFontSmoothing: 'antialiased',
            MozOsxFontSmoothing: 'grayscale',
            textRendering: 'optimizeLegibility',
            direction: isRTL ? 'rtl' : 'ltr',
            backgroundColor: '#ffffff',
            boxSizing: 'border-box',
            width: '100%',
            overflow: 'hidden'
          }}>
            <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundImage: `url(${headerLogo})`, backgroundSize: '60%', backgroundPosition: 'center top', backgroundRepeat: 'no-repeat', opacity: 0.05, zIndex: 0, pointerEvents: 'none' }} />
            <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
              <img src={`${publicUrl}/HeaderAce.png`} alt="Header" style={{ width: '100%', height: 'auto', objectFit: 'contain', marginBottom: '8px', borderBottom: '4px double #000', paddingBottom: '8px', display: 'block', flexShrink: 0 }} crossOrigin="anonymous" />
              
              <div style={{ textAlign: 'center', padding: '6px 0 8px', borderBottom: '1px solid #ddd', marginBottom: '4px', direction: isRTL ? 'rtl' : 'ltr', flexShrink: 0 }}>
                <div style={{ fontSize: '16px', fontWeight: '900', color: '#000080' }}>{isRTL ? 'تقرير الورديات' : 'Shifts Report'}</div>
                <div style={{ fontSize: '11px', color: '#666', marginTop: '3px' }}>{isRTL ? 'تاريخ التقرير' : 'Report Date'}: {new Date().toLocaleDateString('en-US')} — {isRTL ? 'صفحة' : 'Page'} {chunkIdx + 1} / {totalPages}</div>
                
                {hasActiveFilters && (
                  <div style={{ 
                    fontSize: '9px', 
                    color: '#444', 
                    marginTop: '6px', 
                    display: 'flex', 
                    gap: '12px', 
                    justifyContent: 'center', 
                    flexWrap: 'wrap',
                    backgroundColor: 'rgba(238, 242, 247, 0.5)',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    border: '1px solid #ddd'
                  }}>
                    {searchTerm.trim() && <span>{t('common.search', { defaultValue: 'بحث' })}: {searchTerm.trim().replace(/\s+/g, ' ')}</span>}
                    {statusFilter !== 'all' && (
                      <span>{t('shifts.table.status')}: {statusFilter === 'active' ? t('shifts.status.active') : t('shifts.status.inactive')}</span>
                    )}
                  </div>
                )}
              </div>

              <div style={{ marginTop: '12px', direction: isRTL ? 'rtl' : 'ltr', fontFamily: '"Times New Roman", "Times", serif', flex: 1 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #000', backgroundColor: 'rgba(255,255,255,0.7)', tableLayout: 'fixed' }}>
                  <colgroup>
                    <col style={{ width: '5%' }} /><col style={{ width: '27%' }} /><col style={{ width: '23%' }} />
                    <col style={{ width: '12%' }} /><col style={{ width: '12%' }} /><col style={{ width: '10%' }} /><col style={{ width: '11%' }} />
                  </colgroup>
                  <thead>
                    <tr style={{ backgroundColor: 'rgba(238,242,247,0.7)' }}>
                      <th style={colStyles.serial}>#</th>
                      <th style={colStyles.name}>{t('shifts.table.name')}</th>
                      <th style={colStyles.workDays}>{t('shifts.table.workDays')}</th>
                      <th style={colStyles.startTime}>{t('shifts.table.startTime')}</th>
                      <th style={colStyles.endTime}>{t('shifts.table.endTime')}</th>
                      <th style={colStyles.status}>{t('shifts.table.status')}</th>
                      <th style={colStyles.created}>{isRTL ? 'تاريخ الإنشاء' : 'Created'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {chunk.map((shift, idx) => {
                      const foundIdx = allShiftsForPdf.findIndex(s => String(s?.id) === String(shift?.id));
                      const serial = foundIdx >= 0 ? foundIdx + 1 : chunkIdx * 12 + idx + 1;
                      const cellBgColor = idx % 2 === 0 ? 'rgba(255,255,255,0.7)' : 'rgba(245,245,245,0.5)';
                      return (
                        <tr key={shift.id} style={{ backgroundColor: cellBgColor }}>
                          <td style={{ ...colStyles.serial, backgroundColor: cellBgColor }}>{serial}</td>
                          <td style={{ ...colStyles.name, backgroundColor: cellBgColor }}>{getPreviewShiftName(shift)}</td>
                          <td style={{ ...colStyles.workDays, backgroundColor: cellBgColor }}>{getPreviewWorkDaysList(shift)}</td>
                          <td style={{ ...colStyles.startTime, backgroundColor: cellBgColor }}>{getPreviewStartTime(shift)}</td>
                          <td style={{ ...colStyles.endTime, backgroundColor: cellBgColor }}>{getPreviewEndTime(shift)}</td>
                          <td style={{ ...colStyles.status, backgroundColor: cellBgColor }}>{shift.is_active ? t('shifts.status.active') : t('shifts.status.inactive')}</td>
                          <td style={{ ...colStyles.created, backgroundColor: cellBgColor }}>{previewFormatDate(shift.created_at)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div style={{ position: 'absolute', bottom: '10px', left: '18px', right: '18px', flexShrink: 0 }}>
                <img src={`${publicUrl}/FooterAce.png`} alt="Footer" style={{ width: '100%', height: 'auto', objectFit: 'contain', display: 'block', borderTop: '4px double #000', paddingTop: '8px' }} crossOrigin="anonymous" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const preloadImage = (src) => new Promise(resolve => {
    if (!src) return resolve();
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve();
    img.onerror = () => resolve();
    img.src = src;
  });

  const calculateDynamicChunks = (items) => {
    const tableContainer = document.getElementById('shifts-measurement-container');
    const thead = tableContainer ? tableContainer.querySelector('thead') : null;
    const theadHeight = thead ? thead.offsetHeight : 35;
    const rows = tableContainer ? tableContainer.querySelectorAll('tbody tr') : [];
    
    const MAX_TABLE_HEIGHT = 740;
    const chunks = [];
    let currentChunk = [];
    let currentHeight = theadHeight;

    items.forEach((item, index) => {
      const rowElement = rows[index];
      let rowHeight = 45; // Default fallback height
      
      if (rowElement && rowElement.offsetHeight > 0) {
        rowHeight = rowElement.offsetHeight;
      } else {
        const nameLen = getShiftName(item).length;
        const workDaysLen = getWorkDaysList(item).length;
        const maxTextLen = Math.max(nameLen / 25, workDaysLen / 20);
        const lines = Math.max(1, Math.ceil(maxTextLen));
        rowHeight = 35 + (lines * 15);
      }

      if (currentHeight + rowHeight > MAX_TABLE_HEIGHT && currentChunk.length > 0) {
        chunks.push(currentChunk);
        currentChunk = [];
        currentHeight = theadHeight;
      }
      currentChunk.push(item);
      currentHeight += rowHeight;
    });

    if (currentChunk.length > 0) {
      chunks.push(currentChunk);
    }
    return chunks;
  };

  const handleAdd = () => navigate('/shifts/add');
  const handleEdit = (id) => navigate(`/shifts/edit/${id}`);
  const handleView = (id) => navigate(`/shifts/details/${id}`);

  const handleDelete = (shift) => {
    setShiftToDelete(shift);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (shiftToDelete) {
      const idToDelete = shiftToDelete.id;
      setRawShifts(prev => prev.filter(shift => shift.id !== idToDelete));
      setShowDeleteModal(false);
      const backup = shiftToDelete;
      setShiftToDelete(null);
      try {
        const result = await dispatch(deleteWorkShift(idToDelete));
        if (deleteWorkShift.fulfilled.match(result)) {
          fetchAllShifts();
        } else if (deleteWorkShift.rejected.match(result)) {
          setRawShifts(prev => [...prev, backup].sort((a, b) => (a.id && b.id) ? a.id - b.id : 0));
        }
      } catch {
        setRawShifts(prev => [...prev, backup].sort((a, b) => (a.id && b.id) ? a.id - b.id : 0));
      }
    }
  };

  const handlePrintShift = async (shift) => {
    try {
      setShiftForPrint(shift);
      setPdfType('single');
      await new Promise((resolve) => requestAnimationFrame(() => resolve()));
      await new Promise((resolve) => requestAnimationFrame(() => resolve()));

      if (document.fonts && document.fonts.ready) await document.fonts.ready;

      await Promise.all([
        preloadImage(`${publicUrl}/HeaderAce.png`),
        preloadImage(`${publicUrl}/FooterAce.png`),
        preloadImage(headerLogo),
      ]);
      await new Promise((resolve) => setTimeout(resolve, 400));

      setIsGeneratingPdf(true);

      const element = document.getElementById(`shift-pdf-${shift.id}`);
      if (!element) {
        toast.error(t('shifts.print.error', { defaultValue: 'خطأ في تحميل قالب الطباعة' }), { rtl: isRTL });
        return;
      }

      const devicePixelRatio = window.devicePixelRatio || 1;
      const scale = Math.max(3, devicePixelRatio * 2);

      const canvas = await html2canvas(element, {
        scale,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        letterRendering: false,
        allowTaint: false,
        removeContainer: false,
        imageTimeout: 15000,
        onclone: (clonedDoc) => {
          const clonedElement = clonedDoc.getElementById(`shift-pdf-${shift.id}`);
          if (clonedElement) {
            clonedElement.style.fontFamily = '"Times New Roman Bold", "Times New Roman", "Times", serif';
            clonedElement.style.fontWeight = 'bold';
            clonedElement.style.direction = isRTL ? 'rtl' : 'ltr';
            clonedElement.style.setProperty('-webkit-font-smoothing', 'antialiased');
            clonedElement.style.setProperty('-moz-osx-font-smoothing', 'grayscale');
            clonedElement.style.textRendering = 'optimizeLegibility';
            clonedElement.querySelectorAll('*').forEach(el => {
              if (el.style) {
                el.style.fontFamily = '"Times New Roman Bold", "Times New Roman", "Times", serif';
                el.style.fontWeight = 'bold';
              }
            });
          }
        }
      });

      if (!canvas || canvas.width === 0 || canvas.height === 0) {
        toast.error(t('shifts.print.error', { defaultValue: 'حدث خطأ أثناء إنشاء PDF' }), { rtl: isRTL });
        return;
      }

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgData = canvas.toDataURL('image/png', 1.0);
      let imgWidth = pdfWidth;
      let imgHeight = (canvas.height * pdfWidth) / canvas.width;

      const tolerance = 10;
      if (imgHeight > pdfHeight && imgHeight <= pdfHeight + tolerance) {
        const scaleFactor = pdfHeight / imgHeight;
        imgHeight = pdfHeight;
        imgWidth = pdfWidth * scaleFactor;
        const xOffset = (pdfWidth - imgWidth) / 2;
        pdf.addImage(imgData, 'PNG', xOffset, 0, imgWidth, imgHeight, undefined, 'FAST');
      } else if (imgHeight <= pdfHeight) {
        pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight, undefined, 'FAST');
      } else {
        let position = 0;
        while (position < imgHeight) {
          if (position > 0) pdf.addPage();
          pdf.addImage(imgData, 'PNG', 0, -position, imgWidth, imgHeight, undefined, 'FAST');
          position += pdfHeight;
        }
      }

      const identifier = shift.name || shift.id;
      const blob = pdf.output('blob');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ACE_Shift_${identifier}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Shift PDF error:', err);
      toast.error(t('shifts.print.error', { defaultValue: 'حدث خطأ أثناء إنشاء PDF' }), { rtl: isRTL });
    } finally {
      setIsGeneratingPdf(false);
      setShiftForPrint(null);
      setPdfType(null);
    }
  };

  const handlePreviewAll = async () => {
    if (!filteredData || filteredData.length === 0) {
      toast.error(t('shifts.print.noData', { defaultValue: 'لا توجد بيانات للمعاينة' }), { rtl: isRTL });
      return;
    }
    setAllShiftsForPdf(filteredData);
    await new Promise((resolve) => setTimeout(resolve, 300));
    const chunks = calculateDynamicChunks(filteredData);
    setPdfChunks(chunks);
    setPdfType('all');
    setIsPreviewOpen(true);
  };

  const handlePrintAll = async () => {
    if (!filteredData || filteredData.length === 0) {
      toast.error(t('shifts.print.noData', { defaultValue: 'لا توجد بيانات للطباعة' }), { rtl: isRTL });
      return;
    }

    try {
      setAllShiftsForPdf(filteredData);
      await new Promise((resolve) => setTimeout(resolve, 300));

      const chunks = calculateDynamicChunks(filteredData);
      setPdfChunks(chunks);
      setPdfType('all');

      await new Promise((resolve) => requestAnimationFrame(resolve));
      await new Promise((resolve) => requestAnimationFrame(resolve));

      if (document.fonts && document.fonts.ready) await document.fonts.ready;

      await Promise.all([
        preloadImage(`${publicUrl}/HeaderAce.png`),
        preloadImage(`${publicUrl}/FooterAce.png`),
        preloadImage(headerLogo),
      ]);

      await new Promise((resolve) => setTimeout(resolve, 800));
      setIsGeneratingPdf(true);

      const pdf = new jsPDF('p', 'mm', 'a4');
      const devicePixelRatio = window.devicePixelRatio || 1;
      const scale = Math.min(2, Math.max(1.5, devicePixelRatio));

      for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
        await new Promise((resolve) => requestAnimationFrame(resolve));
        await new Promise((resolve) => requestAnimationFrame(resolve));
        await new Promise((resolve) => setTimeout(resolve, 100));

        const element = document.getElementById(`shifts-pdf-all-${chunkIndex}`);
        if (!element) continue;

        const canvas = await html2canvas(element, {
          scale,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
          letterRendering: false,
          allowTaint: false,
          removeContainer: false,
          imageTimeout: 10000,
          scrollX: 0,
          scrollY: 0,
          onclone: (clonedDoc) => {
            const clonedElement = clonedDoc.getElementById(`shifts-pdf-all-${chunkIndex}`);
            if (clonedElement) {
              clonedElement.style.fontFamily = '"Times New Roman Bold", "Times New Roman", "Times", serif';
              clonedElement.style.fontWeight = 'bold';
              clonedElement.style.direction = 'ltr';
              clonedElement.style.left = '0';
              clonedElement.style.top = '0';
              clonedElement.style.position = 'relative';
            }
          }
        });

        const imgData = canvas.toDataURL('image/png', 1.0);

        if (chunkIndex > 0) {
          pdf.addPage('a4', 'p');
        }

        pdf.addImage(imgData, 'PNG', 0, 0, 210, 297, undefined, 'FAST');
      }

      const blob = pdf.output('blob');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ACE_Shifts_All_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success(t('shifts.print.success', { defaultValue: 'تم تنزيل PDF بنجاح' }), { rtl: isRTL });
    } catch (error) {
      console.error('Shifts PDF error:', error);
      toast.error(t('shifts.print.error', { defaultValue: 'حدث خطأ أثناء إنشاء ملف الورديات' }), { rtl: isRTL });
    } finally {
      setIsGeneratingPdf(false);
      setPdfType(null);
      setPdfChunks([]);
    }
  };

  const handleExportShiftToExcel = (shift) => {
    const data = [{
      [t('shifts.table.name')]: getShiftName(shift),
      [t('shifts.table.workDays')]: getWorkDaysList(shift),
      [t('shifts.table.startTime')]: getStartTime(shift),
      [t('shifts.table.endTime')]: getEndTime(shift),
      [t('shifts.table.status')]: shift.is_active ? t('shifts.status.active') : t('shifts.status.inactive')
    }];

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t('shifts.excel.sheetName', { defaultValue: 'Shift' }));
    const identifier = getShiftName(shift) || shift.id;
    XLSX.writeFile(wb, `${t('shifts.excel.fileName', { defaultValue: 'Shift' })}_${identifier}_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success(t('shifts.export.success', { defaultValue: 'تم تصدير البيانات بنجاح' }), { rtl: isRTL });
  };

  const handleExportAllToExcel = () => {
    if (!filteredData || filteredData.length === 0) {
      toast.error(t('shifts.export.noData', { defaultValue: 'لا توجد بيانات للتصدير' }), { rtl: isRTL });
      return;
    }

    const data = filteredData.map((shift, index) => ({
      [t('common.serialNumber', { defaultValue: '#' })]: index + 1,
      [t('shifts.table.name')]: getShiftName(shift),
      [t('shifts.table.workDays')]: getWorkDaysList(shift),
      [t('shifts.table.startTime')]: getStartTime(shift),
      [t('shifts.table.endTime')]: getEndTime(shift),
      [t('shifts.table.offDays', { defaultValue: 'أيام الراحة' })]: shift.details && shift.details.length > 0
        ? shift.details.filter(d => d.is_off_day).map(d => dayNames[d.day_of_week] || d.day_of_week).join(', ') || t('shifts.noOffDays')
        : t('shifts.noOffDays'),
      [t('shifts.table.createdBy', { defaultValue: 'تاريخ الإنشاء' })]: formatDate(shift.created_at),
      [t('shifts.table.status')]: shift.is_active ? t('shifts.status.active') : t('shifts.status.inactive')
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, isRTL ? 'الورديات' : 'Shifts');
    XLSX.writeFile(wb, `${isRTL ? 'الورديات' : 'Shifts'}_${new Date().toISOString().split('T')[0]}.xlsx`);
    toast.success(t('shifts.export.success', { defaultValue: 'تم تصدير البيانات بنجاح' }), { rtl: isRTL });
  };


  return (
    <div style={{ padding: '24px', backgroundColor: '#14263b', minHeight: 'calc(100vh - 80px)', color: 'white', direction: dir, textAlign, overflowX: 'hidden', boxSizing: 'border-box' }}>

      {/* ── Row 1: Title + Add ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', gap: '12px', flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: '22px', fontWeight: 'bold', margin: 0, color: 'white' }}>
          {t('shifts.title')}
        </h1>
        <button
          onClick={handleAdd}
          style={{ backgroundColor: '#AC2000', color: 'white', border: 'none', padding: '10px 18px', borderRadius: '8px', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap' }}
        >
          <FaPlus />
          {t('shifts.addButton')}
        </button>
      </div>

      {/* ── Row 2: Search + Sort + Per-page ── */}
      <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1', minWidth: '200px' }}>
          <FaSearch style={{ position: 'absolute', [isRTL ? 'right' : 'left']: '14px', top: '50%', transform: 'translateY(-50%)', color: '#666', fontSize: '14px' }} />
          <input
            type="text"
            placeholder={t('shifts.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setLocalCurrentPage(1); }}
            style={{ width: '100%', padding: isRTL ? '10px 42px 10px 14px' : '10px 14px 10px 42px', backgroundColor: '#1b3350', border: '1px solid #333', borderRadius: '8px', color: 'white', fontSize: '14px', textAlign: isRTL ? 'right' : 'left', boxSizing: 'border-box' }}
          />
        </div>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 140, color: 'white', fontSize: 13, direction: isRTL ? 'rtl' : 'ltr' }}>
          <span>{t('shifts.filter.status', { defaultValue: 'فلترة حسب الحالة' })}</span>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setLocalCurrentPage(1); }}
            style={{ padding: '10px 12px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: '8px', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr' }}
          >
            <option value="all">{t('shifts.filter.all', { defaultValue: 'الكل' })}</option>
            <option value="active">{t('shifts.status.active', { defaultValue: 'نشط' })}</option>
            <option value="inactive">{t('shifts.status.inactive', { defaultValue: 'غير نشط' })}</option>
          </select>
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 160, color: 'white', fontSize: 13, direction: isRTL ? 'rtl' : 'ltr' }}>
          <span>{t('common.sortBy', { defaultValue: 'ترتيب حسب' })}</span>
          <select
            value={sortField}
            onChange={(e) => setSortField(e.target.value)}
            style={{ padding: '10px 12px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderRadius: '8px', color: 'white', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr' }}
          >
            <option value="name">{t('shifts.table.name', { defaultValue: 'الاسم' })}</option>
            <option value="is_active">{t('shifts.table.status', { defaultValue: 'الحالة' })}</option>
          </select>
        </label>

        <SortOrderSelect value={sortOrder} onChange={(val) => setSortOrder(val)} />

        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
          <label style={{ color: '#aaa', fontSize: '13px', whiteSpace: 'nowrap' }}>
            {t('common.perPage', { defaultValue: 'عدد السجلات' })}:
          </label>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <button
              onClick={() => setPerPageInput(v => Math.max(1, toPositiveInteger(v, perPage) - 1))}
              style={{ backgroundColor: '#1b3350', color: 'white', border: '1px solid #333', padding: '6px 10px', borderRadius: '6px 0 0 6px', cursor: 'pointer', fontSize: '14px', minWidth: '30px' }}
            >−</button>
            <input
              type="number"
              min="1"
              step="1"
              value={perPageInput}
              onChange={(e) => setPerPageInput(toPositiveInteger(e.target.value, perPage))}
              style={{ width: '60px', padding: '6px 8px', backgroundColor: '#1a1f2e', border: '1px solid #333', borderLeft: 'none', borderRight: 'none', borderRadius: 0, color: 'white', fontSize: '14px', textAlign: 'center' }}
            />
            <button
              onClick={() => setPerPageInput(v => toPositiveInteger(v, perPage) + 1)}
              style={{ backgroundColor: '#1b3350', color: 'white', border: '1px solid #333', padding: '6px 10px', borderRadius: '0 6px 6px 0', cursor: 'pointer', fontSize: '14px', minWidth: '30px' }}
            >+</button>
          </div>
          <button
            onClick={handleApplyPerPage}
            style={{ backgroundColor: '#AC2000', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 'bold', whiteSpace: 'nowrap' }}
          >
            {t('common.apply', { defaultValue: 'تطبيق' })}
          </button>
        </div>
      </div>

      {/* ── Row 3: Export / Print ── */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <button
          onClick={handlePreviewAll}
          style={{ backgroundColor: '#444', color: 'white', border: 'none', padding: '9px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
        >
          <FaEye /> {t('shifts.preview.all', { defaultValue: 'معاينة PDF' })}
        </button>
        <button
          onClick={handlePrintAll}
          disabled={isGeneratingPdf}
          style={{ backgroundColor: isGeneratingPdf ? '#666' : '#17a2b8', color: 'white', border: 'none', padding: '9px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: isGeneratingPdf ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '6px', opacity: isGeneratingPdf ? 0.6 : 1 }}
        >
          <FaPrint />
          {isGeneratingPdf ? t('shifts.print.generating', { defaultValue: 'جاري...' }) : t('shifts.print.all', { defaultValue: 'طباعة PDF' })}
        </button>
        <button
          onClick={handleExportAllToExcel}
          style={{ backgroundColor: '#28a745', color: 'white', border: 'none', padding: '9px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
        >
          <FaFileExcel /> {t('shifts.export.all', { defaultValue: 'تصدير Excel' })}
        </button>
      </div>

      {/* ── Table ── */}
      <div style={{ backgroundColor: '#1b3350', borderRadius: '12px', border: '1px solid #333', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, minWidth: '900px' }}>
            <thead>
              <tr style={{ backgroundColor: '#AC2000' }}>
                {['#', t('shifts.table.name'), t('shifts.table.workDays'), t('shifts.table.offDays'), t('shifts.table.createdBy'), t('shifts.table.status'), t('shifts.table.actions')].map((label, i) => (
                  <th key={i} style={{ padding: '18px 16px', textAlign: 'center', color: 'white', fontWeight: 'bold', fontSize: '14px', borderBottom: '2px solid #333', backgroundColor: '#AC2000', whiteSpace: 'nowrap' }}>
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr><td colSpan="7" style={{ padding: '40px', textAlign: 'center', color: 'white' }}>{t('shifts.loading')}</td></tr>
              ) : filteredData.length === 0 ? (
                <tr><td colSpan="7" style={{ padding: '40px', textAlign: 'center', color: 'white' }}>{t('shifts.noRecords')}</td></tr>
              ) : (
                paginatedShifts.map((shift, index) => {
                  const serialNumber = (localCurrentPage - 1) * perPage + index + 1;
                  return (
                    <tr key={shift.id} style={{ borderBottom: '1px solid #333' }}>
                      <td style={{ padding: '15px 16px', color: 'white', fontWeight: 'bold', textAlign: 'center' }}>{serialNumber}</td>
                      <td style={{ padding: '15px 16px', color: 'white', fontWeight: '500', textAlign: 'center' }}>{getShiftName(shift)}</td>
                      <td style={{ padding: '15px 16px', color: 'white', textAlign: 'center' }}>
                        {shift.work_days_count ? `${shift.work_days_count} ${t('shifts.days')}` : '--'}
                      </td>
                      <td style={{ padding: '15px 16px', color: 'white', textAlign: 'center' }}>
                        {shift.details && shift.details.length > 0
                          ? shift.details.filter(d => d.is_off_day).map(d => dayNames[d.day_of_week] || d.day_of_week).join(', ') || t('shifts.noOffDays')
                          : t('shifts.noOffDays')}
                      </td>
                      <td style={{ padding: '15px 16px', color: 'white', textAlign: 'center' }}>{formatDate(shift.created_at)}</td>
                      <td style={{ padding: '15px 16px', textAlign: 'center' }}>
                        <span style={{ backgroundColor: shift.is_active ? '#AC2000' : '#dc3545', color: 'white', padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 'bold' }}>
                          {shift.is_active ? t('shifts.status.active') : t('shifts.status.inactive')}
                        </span>
                      </td>
                      <td style={{ padding: '15px 16px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'nowrap' }}>
                          <button onClick={() => handleView(shift.id)} style={{ backgroundColor: '#666', color: 'white', border: 'none', padding: '8px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', flexShrink: 0 }} title={t('shifts.actions.view')}><FaEye /></button>
                          <button onClick={() => handlePrintShift(shift)} disabled={isGeneratingPdf} style={{ backgroundColor: isGeneratingPdf ? '#555' : '#17a2b8', color: 'white', border: 'none', padding: '8px 10px', borderRadius: '6px', cursor: isGeneratingPdf ? 'not-allowed' : 'pointer', fontSize: '13px', opacity: isGeneratingPdf ? 0.6 : 1, flexShrink: 0 }} title={t('shifts.print.single', { defaultValue: 'طباعة' })}><FaPrint /></button>
                          <button onClick={() => handleExportShiftToExcel(shift)} style={{ backgroundColor: '#28a745', color: 'white', border: 'none', padding: '8px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', flexShrink: 0 }} title={t('shifts.export.single', { defaultValue: 'تصدير Excel' })}><FaFileExcel /></button>
                          <button onClick={() => handleEdit(shift.id)} style={{ backgroundColor: '#AC2000', color: 'white', border: 'none', padding: '8px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', flexShrink: 0 }} title={t('shifts.actions.edit')}><FaEdit /></button>
                          <button onClick={() => handleDelete(shift)} style={{ backgroundColor: '#dc3545', color: 'white', border: 'none', padding: '8px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', flexShrink: 0 }} title={t('shifts.actions.delete')}><FaTrash /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ padding: '20px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', borderTop: '1px solid #333' }}>
            <button onClick={() => setLocalCurrentPage(p => Math.max(1, p - 1))} disabled={localCurrentPage === 1}
              style={{ padding: '8px 12px', backgroundColor: localCurrentPage === 1 ? '#333' : '#1b3350', color: 'white', border: '1px solid #333', borderRadius: '6px', cursor: localCurrentPage === 1 ? 'not-allowed' : 'pointer' }}>
              &lt;&lt;
            </button>
            {(() => {
              const pages = [];
              const showPages = 5;
              let start = Math.max(1, localCurrentPage - Math.floor(showPages / 2));
              let end = Math.min(totalPages, start + showPages - 1);
              if (end - start < showPages - 1) {
                start = Math.max(1, end - showPages + 1);
              }
              for (let i = start; i <= end; i++) {
                pages.push(i);
              }
              return pages.map(page => (
                <button
                  key={page}
                  onClick={() => setLocalCurrentPage(page)}
                  style={{
                    padding: '8px 12px',
                    backgroundColor: page === localCurrentPage ? '#AC2000' : '#1b3350',
                    color: 'white',
                    border: page === localCurrentPage ? 'none' : '1px solid #333',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: page === localCurrentPage ? 'bold' : 'normal'
                  }}
                >
                  {page}
                </button>
              ));
            })()}
            <button onClick={() => setLocalCurrentPage(p => Math.min(totalPages, p + 1))} disabled={localCurrentPage === totalPages}
              style={{ padding: '8px 12px', backgroundColor: localCurrentPage === totalPages ? '#333' : '#1b3350', color: 'white', border: '1px solid #333', borderRadius: '6px', cursor: localCurrentPage === totalPages ? 'not-allowed' : 'pointer' }}>
              &gt;&gt;
            </button>
          </div>
        )}
      </div>

      {/* Delete Modal */}
      {showDeleteModal && ReactDOM.createPortal(
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 999999 }}>
          <div style={{ backgroundColor: '#1b3350', borderRadius: '12px', padding: '30px', maxWidth: '400px', width: '90%', border: '1px solid #333', direction: dir }}>
            <h3 style={{ color: 'white', marginBottom: '20px', fontSize: '18px', fontWeight: 'bold', textAlign: 'center' }}>{t('shifts.deleteModal.title')}</h3>
            <p style={{ color: 'white', marginBottom: '10px', textAlign: 'center', fontSize: '16px' }}>{t('shifts.deleteModal.message', { name: getShiftName(shiftToDelete) })}</p>
            <p style={{ color: 'white', marginBottom: '30px', textAlign: 'center', fontSize: '14px' }}>{t('shifts.deleteModal.warning')}</p>
            <div style={{ display: 'flex', gap: '15px', justifyContent: 'center', flexDirection: isRTL ? 'row' : 'row-reverse' }}>
              <button onClick={() => setShowDeleteModal(false)} style={{ backgroundColor: '#666', color: 'white', border: 'none', padding: '12px 24px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' }}>{t('shifts.deleteModal.cancel')}</button>
              <button onClick={confirmDelete} style={{ backgroundColor: '#dc3545', color: 'white', border: 'none', padding: '12px 24px', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' }}>{t('shifts.deleteModal.confirm')}</button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ── Single Shift Print Template (hidden) ── */}
      {shiftForPrint && pdfType === 'single' && (
        <div
          id={`shift-pdf-${shiftForPrint.id}`}
          aria-hidden="true"
          style={{ position: 'absolute', left: '-9999px', top: 0, width: '794px', minHeight: '1123px', height: 'auto', backgroundColor: '#ffffff', color: '#000000', fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif', fontWeight: 'bold', padding: '40px', boxSizing: 'border-box', overflow: 'visible' }}
        >
          <div style={{ border: '5px solid #000', padding: '3px', backgroundColor: '#ffffff', boxSizing: 'border-box', width: '100%', minHeight: '1043px', height: 'auto', overflow: 'visible' }}>
            <div style={{ border: '2px solid #000', minHeight: '1033px', height: '1033px', padding: '18px 18px 10px', position: 'relative', display: 'flex', flexDirection: 'column', fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif', fontWeight: 'bold', WebkitFontSmoothing: 'antialiased', MozOsxFontSmoothing: 'grayscale', textRendering: 'optimizeLegibility', direction: isRTL ? 'rtl' : 'ltr', backgroundColor: '#ffffff', boxSizing: 'border-box', width: '100%', overflow: 'visible' }}>
              <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', backgroundImage: `url(${headerLogo})`, backgroundSize: '95%', backgroundPosition: 'center center', backgroundRepeat: 'no-repeat', opacity: 0.08, zIndex: 0, pointerEvents: 'none' }} />
              <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', height: '100%', minHeight: '100%' }}>
                <img src={`${publicUrl}/HeaderAce.png`} alt="Header" style={{ width: '100%', height: 'auto', objectFit: 'contain', marginBottom: '8px', borderBottom: '4px double #000', paddingBottom: '8px', flexShrink: 0 }} crossOrigin="anonymous" />

                <div style={{ textAlign: 'center', padding: '6px 0 8px', borderBottom: '1px solid #ddd', marginBottom: '4px', flexShrink: 0, direction: isRTL ? 'rtl' : 'ltr' }}>
                  <div style={{ fontSize: '16px', fontWeight: '900', color: '#000080' }}>{isRTL ? 'تقرير الورديات' : 'Shifts Report'}</div>
                  <div style={{ fontSize: '11px', color: '#666', marginTop: '3px' }}>ID: #{shiftForPrint.id}</div>
                </div>

                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'visible', minHeight: 0, paddingBottom: '120px' }}>
                  <div style={{ marginTop: '12px', border: '2px solid #000', borderRadius: '4px', overflow: 'hidden', fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif', fontWeight: 'bold', backgroundColor: 'rgba(255,255,255,0.3)' }}>
                    {[
                      { label: isRTL ? 'رقم التعريف' : 'ID', value: `#${shiftForPrint.id}`, accent: true, required: true },
                      { label: t('shifts.print.labels.name'), value: getShiftName(shiftForPrint), required: true },
                      { label: t('shifts.print.labels.workDays'), value: getWorkDaysList(shiftForPrint), required: true },
                      { label: t('shifts.print.labels.startTime'), value: getStartTime(shiftForPrint), required: true },
                      { label: t('shifts.print.labels.endTime'), value: getEndTime(shiftForPrint), required: true },
                      { label: isRTL ? 'أيام الراحة' : 'Off Days', value: shiftForPrint.details ? shiftForPrint.details.filter(d => d.is_off_day).map(d => dayNames[d.day_of_week] || d.day_of_week).join(', ') || '—' : '—' },
                      { label: isRTL ? 'تاريخ الإنشاء' : 'Created At', value: formatDate(shiftForPrint.created_at) },
                      { label: t('shifts.print.labels.status'), value: shiftForPrint.is_active ? t('shifts.status.active') : t('shifts.status.inactive'), accent: true, required: true }
                    ].filter(row => row.required || (row.value && row.value !== '—')).map((row) => (
                      <div key={row.label} style={{ display: 'flex', borderBottom: '1px solid #000', minHeight: '38px' }}>
                        <div style={{ width: '30%', borderLeft: '1px solid #000', backgroundColor: 'rgba(227,242,253,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left', fontWeight: '700', fontSize: '14px', direction: isRTL ? 'rtl' : 'ltr', color: '#1976d2', fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif', padding: '0 10px' }}>
                          {row.label}
                        </div>
                        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left', fontSize: '15px', fontWeight: row.accent ? '700' : '500', color: row.accent ? '#0057c2' : '#000', direction: isRTL ? 'rtl' : 'ltr', padding: '0 10px', backgroundColor: 'rgba(255,255,255,0.3)', fontFamily: '"Times New Roman Bold", "Times New Roman", "Times", serif' }}>
                          {row.value}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div style={{ position: 'absolute', bottom: '10px', left: '18px', right: '18px', flexShrink: 0 }}>
                  <img src={`${publicUrl}/FooterAce.png`} alt="Footer" style={{ width: '100%', height: 'auto', objectFit: 'contain', borderTop: '4px double #000', paddingTop: '8px' }} crossOrigin="anonymous" />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── All Shifts Print Templates (hidden) ── */}
      {pdfType === 'all' && pdfChunks && pdfChunks.length > 0 && pdfChunks.map((chunk, chunkIndex) => (
        <div
          key={chunkIndex}
          style={{
            position: 'absolute',
            left: '-9999px',
            top: 0
          }}
        >
          {renderPdfPage(chunk, chunkIndex, pdfChunks.length, `shifts-pdf-all-${chunkIndex}`)}
        </div>
      ))}

      {/* ── Preview Modal ── */}
      <PdfPreviewModal
        isOpen={isPreviewOpen && pdfChunks && pdfChunks.length > 0}
        title={isRTL ? 'تقرير الورديات' : 'Shifts Report'}
        onClose={() => { setIsPreviewOpen(false); setPdfChunks([]); setPdfType(null); }}
        onPrint={handlePrintAll}
        isGeneratingPdf={isGeneratingPdf}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', alignItems: 'center' }}>
          {pdfChunks && pdfChunks.length > 0 && pdfChunks.map((chunk, chunkIdx) => (
            <div key={chunkIdx} style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.5)' }}>
              {renderPdfPage(chunk, chunkIdx, pdfChunks.length)}
            </div>
          ))}
        </div>
      </PdfPreviewModal>

      {/* Hidden Measurement Table */}
      <div
        id="shifts-measurement-container"
        style={{
          position: 'absolute',
          left: '-9999px',
          top: 0,
          width: '714px', // 794px - 80px (padding) = 714px to match PDF page container width!
          visibility: 'hidden',
          pointerEvents: 'none'
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid #000', fontFamily: '"Times New Roman", "Times", serif', tableLayout: 'fixed' }}>
          <colgroup>
            <col style={{ width: '5%' }} /><col style={{ width: '27%' }} /><col style={{ width: '23%' }} />
            <col style={{ width: '12%' }} /><col style={{ width: '12%' }} /><col style={{ width: '10%' }} /><col style={{ width: '11%' }} />
          </colgroup>
          <thead>
            <tr style={{ backgroundColor: 'rgba(238,242,247,0.7)' }}>
              <th style={colStyles.serial}>#</th>
              <th style={colStyles.name}>{t('shifts.table.name')}</th>
              <th style={colStyles.workDays}>{t('shifts.table.workDays')}</th>
              <th style={colStyles.startTime}>{t('shifts.table.startTime')}</th>
              <th style={colStyles.endTime}>{t('shifts.table.endTime')}</th>
              <th style={colStyles.status}>{t('shifts.table.status')}</th>
              <th style={colStyles.created}>{isRTL ? 'تاريخ الإنشاء' : 'Created'}</th>
            </tr>
          </thead>
          <tbody>
            {allShiftsForPdf.map((shift, idx) => (
              <tr key={shift.id}>
                <td style={colStyles.serial}>{idx + 1}</td>
                <td style={{ ...colStyles.name, direction: isRTL ? 'rtl' : 'ltr' }}>{isRTL ? (shift.name_ar || shift.name_en || shift.name || '—') : (shift.name_en || shift.name_ar || shift.name || '—')}</td>
                <td style={{ ...colStyles.workDays, direction: isRTL ? 'rtl' : 'ltr' }}>{getWorkDaysList(shift)}</td>
                <td style={colStyles.startTime}>{getStartTime(shift)}</td>
                <td style={colStyles.endTime}>{getEndTime(shift)}</td>
                <td style={colStyles.status}>{shift.is_active ? t('shifts.status.active') : t('shifts.status.inactive')}</td>
                <td style={colStyles.created}>{shift.created_at ? new Date(shift.created_at).toLocaleDateString('en-US') : '--'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Shifts;
